import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AddressType } from 'src/app/models/onboarding/applicant-address.model';
import {
	ApplicantDataAndPreApplicationId,
	ApplicantRecipient,
	CreditParameters,
	CreditSummary,
	PreApplicationState,
} from 'src/app/models/state/pre-application.model';

export const selectPreAplicationState =
	createFeatureSelector<PreApplicationState>('preApplication');

export const selectPreApplicationId = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState.preApplicationId
);

export const selectApplicantName = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState?.applicantName
);

export const selectApplicantFullName = createSelector(
	selectApplicantName,
	applicantName => {
		return applicantName
			? `${applicantName.name} ${applicantName.firstSurname} ${applicantName.secondSurname}`.trim()
			: '';
	}
);

export const selectApplicantOnlyName = createSelector(
	selectApplicantName,
	applicantName => {
		return applicantName ? `${applicantName.name}`.trim() : '';
	}
);

export const selectApplicantPhone = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState.applicantPhone
);

export const selectIsApplicantSameAs = (recipient: ApplicantRecipient) =>
	createSelector(
		selectPreAplicationState,
		selectApplicantName,
		(preApplicationState, applicantName) => {
			const applicantNameIsSameAsBeneficiary =
				applicantName.name === recipient.name &&
				applicantName.firstSurname === recipient.firstSurname &&
				applicantName.secondSurname === recipient.secondSurname;
			const applicantBirthDateIsSameAsBeneficiary =
				preApplicationState.applicantBirthDate === recipient.birthDay;
			return (
				applicantNameIsSameAsBeneficiary &&
				applicantBirthDateIsSameAsBeneficiary
			);
		}
	);

export const selectSubProductId = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState.subProductId
);

export const selectCreditSummary = createSelector(
	selectPreAplicationState,
	preApplicationState => {
		const creditSummary: CreditSummary = {
			authorizedAmount: preApplicationState.authorizedAmount,
			creditTerm: preApplicationState.creditTerm,
			rate: preApplicationState.rate,
			periodPayment: preApplicationState.periodPayment,
			subProductId: preApplicationState.subProductId,
			cat: preApplicationState.cat,
			applicantAmountSelected: preApplicationState.applicantAmountSelected,
			isSocialContributionApplicable:
				preApplicationState.isSocialContributionApplicable,
			preApplicationId: preApplicationState.preApplicationId,
		};
		return creditSummary;
	}
);

export const selectCreditParameters = createSelector(
	selectPreAplicationState,
	preApplicationState => {
		const creditParameters: CreditParameters = {
			periodicityId: preApplicationState.periodicityId,
			amount: preApplicationState.amount,
			creditTerm: preApplicationState.creditTerm,
			subProductId: preApplicationState.subProductId,
			segmentId: preApplicationState.segmentId,
			productId: preApplicationState.productId,
			productCobisId: preApplicationState.productCobisId,
		};

		return creditParameters;
	}
);

export const selectIsApplicantAMember = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState.isApplicantAMember
);

export const selectApplicantDataAndPreApplicationId = createSelector(
	selectPreAplicationState,
	selectApplicantFullName,
	preApplicationState => {
		const applicantData: ApplicantDataAndPreApplicationId = {
			applicantName: preApplicationState.applicantName,
			applicantId: preApplicationState.applicantId,
			applicantBirthDate: preApplicationState.applicantBirthDate,
			applicantPhone: preApplicationState.applicantPhone,
			isApplicantAMember: preApplicationState.isApplicantAMember,
			applicantType: preApplicationState.applicantType,
			preApplicationId: preApplicationState.preApplicationId,
			email: preApplicationState.email,
			curp: preApplicationState.curp,
		};
		return applicantData;
	}
);

export const selectPreApplicationAmount = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState?.amount
);

export const selectApplicantPhoneAndIsApplicantAMember = createSelector(
	selectPreAplicationState,
	preApplicationState => ({
		applicantPhone: preApplicationState?.applicantPhone,
		isApplicantAMember: preApplicationState?.isApplicantAMember,
		applicantId: preApplicationState.applicantId,
	})
);

export const selectPreApplicationAuthorizedAmount = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState?.authorizedAmount
);

export const selectFolio = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState.folio
);

export const selectReferencePhoneIsSameAsApplicants = (
	referencePhone: string
) =>
	createSelector(selectPreAplicationState, preApplicationState => {
		return preApplicationState.applicantPhone === referencePhone;
	});

export const selectPreApplicationIdAndRate = createSelector(
	selectPreAplicationState,
	preApplicationState => ({
		preApplicationId: preApplicationState.preApplicationId,
		rate: preApplicationState.rate,
	})
);

export const selectApprovedCreditData = createSelector(
	selectPreAplicationState,
	selectApplicantFullName,
	selectApplicantName,
	(preApplicationState, applicantFullName, applicantName) => ({
		applicantAmountSelected: preApplicationState.applicantAmountSelected,
		applicantId: preApplicationState.applicantId,
		applicantFullName: applicantFullName,
		applicantName: applicantName,
	})
);

export const selectProductInfo = createSelector(
	selectPreAplicationState,
	preApplicationState => ({
		productId: preApplicationState.productId,
		productName: preApplicationState.productName,
		subProductId: preApplicationState.subProductId,
		segmentId: preApplicationState.segmentId,
	})
);

export const selectProductCobisId = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState.productCobisId
);

export const selectIsSocialContributionApplicable = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState.isSocialContributionApplicable
);

export const selectPreApplicationIdAndFolio = createSelector(
	selectPreAplicationState,
	preApplicationState => ({
		preApplicationId: preApplicationState.preApplicationId,
		folio: preApplicationState.folio,
	})
);

export const selectTdcApplicationId = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState.tdcApplicationId
);

export const selectApplicantId = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState.applicantId
);

export const selectApplicantAddresses = createSelector(
	selectPreAplicationState,
	preApplicationState => preApplicationState.applicantAddresses ?? []
);

export const selectApplicantAddressBy = (addressType: AddressType) =>
	createSelector(selectApplicantAddresses, applicantAddresses => {
		return applicantAddresses?.find(
			address => address.addressType === addressType
		);
	});

export const selectApplicantParticularFullAddress = createSelector(
	selectApplicantAddressBy(AddressType.PARTICULAR),
	applicantAddress => {
		return `${applicantAddress?.street.trim()} ${applicantAddress?.exteriorNumber.trim()} ${applicantAddress?.interiorNumber.trim()}, ${applicantAddress?.colony.trim()}`;
	}
);
